import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { Camera, ModoJogo } from '@core/tipos';
import { novaSemente } from '@core/aleatorio/rng';
import type { IndiceLexico } from '@core/lexico/indice';
import type { Ambiente, EstadoJogo } from '@core/motor/estados';
import { estadoInicial } from '@core/motor/estados';
import type { Evento } from '@core/motor/eventos';
import { reduzir } from '@core/motor/maquina';
import { MSG_FALHA_SALVAR } from '@core/motor/mensagens';
import type { Armazem } from '@persistencia/indexeddb';
import { criarArmazem } from '@persistencia/indexeddb';
import { chaveDaPartida, migrar, serializar } from '@persistencia/schema';

/**
 * Cola entre o motor puro, o dicionário e a persistência.
 *
 * Regras de gravação de §12:
 *  - o draft é salvo depois de 300 ms sem edição;
 *  - jogada confirmada, câmera e preferências salvam na hora;
 *  - falha de gravação **não** interrompe a partida — vira um aviso discreto.
 *
 * Cada modo grava na sua chave (`chaveDaPartida`, ADR-0009). A Trama tem a
 * dela porque a partida do dia sobrevive ao que apaga a cadeia: trocar de modo
 * limpa a sessão em cadeia (§22.5), e o dia de hoje continua resolvido (§23.1).
 * A gravação segue `estado.modo`, e não o modo com que o componente montou —
 * §22.6 troca o modo com a partida em curso.
 */

export const ATRASO_SALVAR_DRAFT = 300;

export interface UsoJogo {
  readonly estado: EstadoJogo;
  readonly despachar: (evento: Evento) => void;
  readonly restaurada: boolean;
  readonly avisoPersistencia: string | undefined;
}

export interface OpcoesJogo {
  readonly indice: IndiceLexico;
  readonly dicionarioPronto: boolean;
  readonly camera: Camera;
  /** Variante da cadeia com que a partida começa (§22). */
  readonly modo?: ModoJogo;
  /** Duração inicial da corrida, em segundos (§22.2). */
  readonly duracaoSegundos?: number;
  readonly armazem?: Armazem;
  readonly entropia?: () => number;
  readonly agora?: () => number;
  /**
   * Sessão salva que não deve voltar — a Trama de um dia que já passou.
   * Descartá-la aqui, antes do motor, evita que o tabuleiro velho pisque na
   * tela até o dia novo chegar.
   */
  readonly sessaoVencida?: (jogo: EstadoJogo) => boolean;
}

export function useJogo(opcoes: OpcoesJogo): UsoJogo {
  const { indice, dicionarioPronto, camera } = opcoes;

  const entropia = opcoes.entropia ?? Math.random;
  const agora = opcoes.agora ?? Date.now;

  const armazem = useMemo(() => opcoes.armazem ?? criarArmazem(), [opcoes.armazem]);

  // O índice não cabe no estado (não é serializável) e muda uma única vez,
  // quando o worker termina. Um ref mantém o redutor puro na assinatura sem
  // recriar o dispatch a cada render.
  const ambienteRef = useRef<Ambiente>({ indice });
  ambienteRef.current = { indice };

  const [estado, despacharBruto] = useReducer(
    (atual: EstadoJogo, evento: Evento) => reduzir(atual, evento, ambienteRef.current),
    undefined,
    () => estadoInicial(novaSemente(entropia), opcoes.modo, opcoes.duracaoSegundos),
  );

  const [restaurada, setRestaurada] = useState(false);
  const [avisoPersistencia, setAviso] = useState<string | undefined>(undefined);

  const despachar = useCallback((evento: Evento) => despacharBruto(evento), []);

  /* ── Restauração + liberação do dicionário (§12, §17) ─────────────────── */

  /*
   * De onde a sessão é lida. Fixado na montagem de propósito: escolher outro
   * modo remonta o jogo (§22.5), e §22.6 troca o modo com a partida já em
   * curso — depois da leitura, portanto. Ler de uma chave que muda em voo
   * restauraria a partida errada por cima da que está sendo jogada.
   */
  const chaveDeLeitura = useRef(chaveDaPartida(opcoes.modo ?? 'zen')).current;

  /*
   * A trava é armada **depois** da leitura, e não antes.
   *
   * Armada antes, o StrictMode do desenvolvimento perdia a restauração inteira:
   * ele roda o efeito, o descarta e o roda de novo, e a primeira passada armava
   * a trava e disparava a leitura que o descarte então jogava fora — a segunda
   * saía cedo, e a partida salva nunca chegava ao motor. Não aparecia na carga
   * da página, onde o dicionário ainda não está pronto e o efeito só corre de
   * verdade mais tarde, uma vez só; aparecia na remontagem por troca de modo
   * (§22.5), que é justamente onde a partida de outro modo tem de voltar.
   *
   * Em produção não havia StrictMode e nada disso se via — o que é pior, não
   * melhor: o desenvolvimento mentia sobre a persistência.
   *
   * Armada depois, o descarte custa uma leitura repetida em desenvolvimento e
   * nada em produção, e as duas se comportam igual.
   */
  const restaurouRef = useRef(false);
  const sessaoVencidaRef = useRef(opcoes.sessaoVencida);
  sessaoVencidaRef.current = opcoes.sessaoVencida;
  useEffect(() => {
    if (!dicionarioPronto || restaurouRef.current) return;

    let cancelado = false;
    void (async () => {
      const bruto = await armazem.ler(chaveDeLeitura);
      if (cancelado) return;
      restaurouRef.current = true;

      const resultado = migrar(bruto);
      if (resultado.ok && sessaoVencidaRef.current?.(resultado.sessao.jogo) !== true) {
        despachar({ tipo: 'dicionario-pronto' });
        despachar({ tipo: 'sessao-restaurada', estado: resultado.sessao.jogo });
        setRestaurada(true);
        return;
      }

      if (!resultado.ok && resultado.motivo === 'incompativel') {
        // §17: preserva uma cópia bruta e começa uma sessão nova.
        void armazem.guardarDiagnostico(resultado.bruto);
        setAviso('A sessão anterior não pôde ser restaurada');
      }
      despachar({ tipo: 'dicionario-pronto' });
    })();

    return () => {
      cancelado = true;
    };
  }, [dicionarioPronto, armazem, despachar, chaveDeLeitura]);

  /* ── Gravação (§12) ───────────────────────────────────────────────────── */

  const anteriorRef = useRef<EstadoJogo>(estado);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (estado.fase === 'carregando') return;

    const anterior = anteriorRef.current;
    anteriorRef.current = estado;

    const mudouAlgoConfirmado =
      anterior.cadeia !== estado.cadeia ||
      anterior.ancora !== estado.ancora ||
      anterior.fase !== estado.fase;

    const gravar = () => {
      const chave = chaveDaPartida(estado.modo);
      void armazem.guardar(chave, serializar(estado, camera, agora())).then((resultado) => {
        setAviso(resultado.ok ? undefined : MSG_FALHA_SALVAR);
      });
    };

    if (timerRef.current !== undefined) clearTimeout(timerRef.current);

    if (mudouAlgoConfirmado) {
      gravar();
      return;
    }

    // Só o draft mudou: espera a digitação assentar.
    timerRef.current = setTimeout(gravar, ATRASO_SALVAR_DRAFT);
    return () => {
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    };
  }, [estado, camera, armazem, agora]);

  return { estado, despachar, restaurada, avisoPersistencia };
}
